import React from 'react';

import AppsIcon from '@atlaskit/icon/core/apps';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { B400, N500 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { type RovoLogoProps } from '../../constants/icons';
import { isNounKey, type NounKeys, nouns } from '../../constants/nouns';
import { type ProductKeys, useProductConfigs } from '../../constants/products';
import {
	getForgeConnectorIcon,
	isForgeConnectorProductKey,
} from '../../constants/schemas/3p-config';

export type ProductIconProps = RovoLogoProps & {
	product?: ProductKeys;
	isSelected?: boolean;
	useSearchResultIcon?: boolean;
};

export type ProductOrNounIconProps = RovoLogoProps & {
	productOrNounKey: ProductKeys | NounKeys | undefined;
	isSelected?: boolean;
};

export const ProductIcon = ({
	product,
	size = 'large',
	isSelected = false,
	useSearchResultIcon = false,
	...props
}: ProductIconProps): React.JSX.Element => {
	const products = useProductConfigs();
	if (product) {
		// First check if it's a Forge connector - prioritize dynamic API icons
		if (isForgeConnectorProductKey(product)) {
			const forgeIcon = getForgeConnectorIcon(product);
			if (forgeIcon) {
				// Forge connector icons are RovoLogo functions, not React components
				return forgeIcon({ ...props, size });
			}
		}
		// Fallback to static products map (covers hardcoded Forge connectors and all other products)
		const config = products[product];
		if (config) {
			const Icon = (useSearchResultIcon && config.SearchResultIcon) || config.Icon;
			if (Icon) {
				return <Icon {...props} size={size} shouldUseNewLogoDesign />;
			}
		}
	}
	// Fallback to AppsIcon
	return (
		<AppsIcon
			label=""
			spacing="spacious"
			color={isSelected ? token('color.icon.selected', B400) : token('color.icon', N500)}
		/>
	);
};

type ProductOrNounOrShowMoreProos = {
	isShowMore?: boolean;
} & ProductOrNounIconProps;

export const IconDecider = ({
	isShowMore,
	productOrNounKey,
	size = 'large',
	isSelected = false,
	...props
}: ProductOrNounOrShowMoreProos): React.JSX.Element => {
	if (isShowMore) {
		return (
			<ShowMoreHorizontalIcon
				label=""
				spacing="spacious"
				color={isSelected ? token('color.icon.selected', B400) : token('color.icon', N500)}
			/>
		);
	}

	return ProductOrNounIcon({
		productOrNounKey,
		size,
		isSelected,
		...props,
	});
};

export const ProductOrNounIcon = ({
	productOrNounKey,
	size = 'large',
	isSelected = false,
	...props
}: ProductOrNounIconProps): React.JSX.Element => {
	const products = useProductConfigs();
	let Icon;

	if (productOrNounKey) {
		const isNounFilter = isNounKey(productOrNounKey);

		if (isNounFilter) {
			Icon = nouns[productOrNounKey].Icon;
		} else {
			// First check if it's a Forge connector - prioritize dynamic API icons
			if (isForgeConnectorProductKey(productOrNounKey)) {
				const forgeIcon = getForgeConnectorIcon(productOrNounKey);
				if (forgeIcon) {
					// Forge connector icons are RovoLogo functions, not React components
					return forgeIcon({ ...props, size });
				}
			}
			// Fallback to static products map (covers hardcoded Forge connectors and all other products)
			const productConfig = products[productOrNounKey as ProductKeys];
			if (productConfig?.Icon) {
				Icon = productConfig.Icon;
			}
		}

		if (Icon) {
			return <Icon {...props} size={size} />;
		}
	}

	// Fallback to AppsIcon
	return (
		<AppsIcon
			label=""
			spacing="spacious"
			color={isSelected ? token('color.icon.selected', B400) : token('color.icon', N500)}
		/>
	);
};
